import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useLanguage } from '@/i18n/LanguageContext';

interface Translation {
  id: string;
  source_language: string;
  target_language: string;
  source_text: string;
  translated_text: string;
  translation_type: 'text' | 'image' | 'voice';
  output_mode?: 'text' | 'voice' | 'both';
  created_at: string;
}

const TranslationHistory = () => {
  const { user } = useAuth();
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const fetchHistory = async () => {
      const { data, error } = await supabase.from('translations').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(20);
      if (!error && data) setTranslations(data as Translation[]);
      setLoading(false);
    };
    fetchHistory();

    const channel = supabase.channel('translations-changes').on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'translations', filter: `user_id=eq.${user?.id}` }, (payload) => {
      setTranslations((prev) => [payload.new as Translation, ...prev]);
    }).subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  if (translations.length === 0) {
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-2xl">{t('history.title')}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t('history.noTranslations')}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0"><p className="text-xs sm:text-sm text-muted-foreground">{t('history.emptyMessage')}</p></CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="text-base sm:text-2xl">{t('history.title')}</CardTitle>
        <CardDescription className="text-xs sm:text-sm">{t('history.recentTranslations')}</CardDescription>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0">
        <ScrollArea className="h-[300px] sm:h-[400px] pr-4">
          <div className="space-y-3 sm:space-y-4">
            {translations.map((translation) => (
              <div key={translation.id} className="p-3 sm:p-4 border rounded-lg hover:bg-muted/50 hover:shadow-sm transition-all">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Badge variant="outline" className="text-[10px] sm:text-xs">{translation.source_language} → {translation.target_language}</Badge>
                    <Badge variant="secondary" className="text-[10px] sm:text-xs">{translation.translation_type}</Badge>
                    {translation.output_mode ? <Badge variant="outline" className="text-[10px] sm:text-xs">{translation.output_mode}</Badge> : null}
                  </div>
                  <span className="text-[10px] sm:text-xs text-muted-foreground">{formatDistanceToNow(new Date(translation.created_at), { addSuffix: true })}</span>
                </div>
                <p className="text-xs sm:text-sm mb-1.5 sm:mb-2 line-clamp-2">{translation.source_text}</p>
                <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{translation.translated_text}</p>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default TranslationHistory;
